import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import { getSupabase, useAuthStore } from './auth';

function debugLog(label, details = null) {
  const prefix = `[ChatStore ${new Date().toISOString()}] ${label}`;
  if (details === null || details === undefined) {
    console.log(prefix);
    return;
  }
  console.log(prefix, details);
}

let saveTimeout = null;

export const useChatStore = defineStore('chat', () => {
  const defaultThread = { id: 'default', name: 'Default', messages: [] };
  const messages = ref(defaultThread.messages);
  const threads = ref([defaultThread]);
  const activeThreadId = ref('default');
  const isLoading = ref(false);
  const isConnected = ref(false);
  const streamingMessage = ref('');
  const progressLines = ref([]);
  const config = ref({
    scope: 'overview',
    book: 'general',
    terminalAccess: true,
  });
  const isLoaded = ref(false);
  let abortController = null;

  const activeThread = computed(() => threads.value.find(t => t.id === activeThreadId.value));

  // ── Persistence ──────────────────────────────────────────────
  async function getAccessToken() {
    const sb = getSupabase();
    if (!sb) return null;
    try {
      const { data: { session } } = await sb.auth.getSession();
      return session?.access_token || null;
    } catch (_) {
      return null;
    }
  }

  async function loadFromServer() {
    // Don't overwrite state while a stream is in progress
    if (isLoading.value) {
      debugLog('chat.load.skip', { reason: 'stream in progress' });
      return;
    }
    try {
      const token = await getAccessToken();

      // Try server first if authenticated
      if (token) {
        const directUrl = typeof window !== 'undefined' && window.OPENCLAW_API_BASE_URL;
        const useProxy = !directUrl || window.OPENCLAW_USE_SAME_ORIGIN_API_PROXY !== false;
        const url = useProxy ? '/api/chat-state' : `${directUrl}/api/chat-state`;

        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          const saved = data.chatStore;

          if (saved) {
            if (saved.threads && Array.isArray(saved.threads) && saved.threads.length > 0) {
              threads.value = saved.threads;
            }
            if (saved.activeThreadId && threads.value.find(t => t.id === saved.activeThreadId)) {
              activeThreadId.value = saved.activeThreadId;
            }
            const active = threads.value.find(t => t.id === activeThreadId.value);
            if (active) {
              messages.value = active.messages || [];
            }
            if (saved.config) {
              config.value = { ...config.value, ...saved.config };
            }
            debugLog('chat.load.success', {
              threadCount: threads.value.length,
              activeThread: activeThreadId.value,
              messageCount: messages.value.length
            });
          }
          isLoaded.value = true;
          return;
        }

        // 401 = token expired or invalid — trigger auth gate so user can re-sign in
        if (response.status === 401) {
          debugLog('chat.load.401', { reason: 'auth required, showing auth gate' });
          try {
            const authStore = useAuthStore();
            authStore.showAuthGate = true;
          } catch (_) {}
        }
      }

      debugLog('chat.load.no-data', { authenticated: !!token });
    } catch (e) {
      debugLog('chat.load.error', { error: e.message });
    } finally {
      isLoaded.value = true;
    }
  }

  function serializeState() {
    return {
      threads: threads.value.map(t => ({
        id: t.id,
        name: t.name,
        messages: (t.messages || []).map(m => ({
          id: m.id,
          role: m.role,
          content: m.content,
          timestamp: m.timestamp,
          // Preserve chart data but strip heavy fields
          ...(m.chart ? { chart: m.chart } : {}),
          ...(m.charts ? { charts: m.charts } : {}),
          // Preserve progress lines for assistant messages
          ...(m.progressLines ? { progressLines: m.progressLines } : {}),
        })),
      })),
      activeThreadId: activeThreadId.value,
      config: config.value,
    };
  }

  async function saveToServer() {
    try {
      const payload = serializeState();

      const token = await getAccessToken();
      if (!token) {
        debugLog('chat.save.skip', { reason: 'no auth token' });
        return;
      }

      const directUrl = typeof window !== 'undefined' && window.OPENCLAW_API_BASE_URL;
      const useProxy = !directUrl || window.OPENCLAW_USE_SAME_ORIGIN_API_PROXY !== false;
      const url = useProxy ? '/api/chat-state' : `${directUrl}/api/chat-state`;

      await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ chatStore: payload }),
      });

      debugLog('chat.save.success', { threadCount: payload.threads.length });
    } catch (e) {
      debugLog('chat.save.error', { error: e.message });
    }
  }

  // Debounced save — avoids hammering the API on every keystroke
  function scheduleSave() {
    if (!isLoaded.value) return;
    if (saveTimeout) clearTimeout(saveTimeout);
    saveTimeout = setTimeout(() => {
      saveToServer();
    }, 1500);
  }

  // Watch for changes and auto-save
  watch(threads, scheduleSave, { deep: true });
  watch(activeThreadId, scheduleSave);

  // ── Message operations ────────────────────────────────────────
  function addMessage(message) {
    const msgWithTimestamp = {
      ...message,
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
    };
    debugLog('chat.message.add', { role: message.role, id: msgWithTimestamp.id });
    // Only push to thread.messages — messages.value is always a ref to the active thread's array
    const thread = threads.value.find(t => t.id === activeThreadId.value);
    if (thread) {
      thread.messages.push(msgWithTimestamp);
    } else {
      messages.value.push(msgWithTimestamp);
    }
    debugLog('chat.messages.count', { count: (thread?.messages || messages.value).length });
  }

  function addProgressLine(text) {
    const time = new Date().toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const line = { time, text };
    progressLines.value.push(line);
    debugLog('chat.progress.add', { time, text });
    return line;
  }

  function clearProgress() {
    progressLines.value = [];
    debugLog('chat.progress.clear', { previousCount: progressLines.value.length });
  }

  function clearMessages() {
    debugLog('chat.messages.clear', { previousCount: messages.value.length });
    const thread = threads.value.find(t => t.id === activeThreadId.value);
    if (thread) {
      thread.messages = [];
      messages.value = thread.messages;
    } else {
      messages.value = [];
    }
    scheduleSave();
  }

  // ── Thread operations ─────────────────────────────────────────
  function createThread(name = 'New Thread') {
    const newId = `thread_${Date.now()}`;
    const newThread = { id: newId, name, messages: [] };
    threads.value.push(newThread);
    activeThreadId.value = newId;
    messages.value = newThread.messages;
    debugLog('chat.thread.create', { id: newId, name });
    scheduleSave();
    return newId;
  }

  function switchThread(threadId) {
    const thread = threads.value.find(t => t.id === threadId);
    if (!thread) return;
    activeThreadId.value = threadId;
    messages.value = thread.messages || [];
    debugLog('chat.thread.switch', { id: threadId, name: thread.name, messageCount: messages.value.length });
    scheduleSave();
  }

  function deleteThread(threadId) {
    if (threadId === 'default') return;
    const index = threads.value.findIndex(t => t.id === threadId);
    if (index === -1) return;
    threads.value.splice(index, 1);
    if (activeThreadId.value === threadId) {
      activeThreadId.value = 'default';
      messages.value = threads.value.find(t => t.id === 'default')?.messages || [];
    }
    debugLog('chat.thread.delete', { id: threadId });
    scheduleSave();
  }

  function renameThread(threadId, name) {
    const thread = threads.value.find(t => t.id === threadId);
    if (!thread) return;
    thread.name = name;
    debugLog('chat.thread.rename', { id: threadId, name });
    scheduleSave();
  }

  // ── Stop streaming ──────────────────────────────────────────────
  function stopStreaming() {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
    // Save any partial streaming message as the assistant response
    if (streamingMessage.value) {
      const msg = { role: 'assistant', content: streamingMessage.value };
      if (progressLines.value.length > 0) {
        msg.progressLines = [...progressLines.value];
      }
      addMessage(msg);
      streamingMessage.value = '';
    }
    isLoading.value = false;
    isConnected.value = false;
    debugLog('chat.stream.stopped');
  }

  // ── Send message ──────────────────────────────────────────────
  async function sendMessage(content) {
    if (!content.trim()) {
      debugLog('chat.send.skipped', { reason: 'empty content' });
      return;
    }

    debugLog('chat.send.start', { content: content.substring(0, 50), config });

    // If already streaming, stop the current stream first
    if (isLoading.value) {
      stopStreaming();
    }

    addMessage({ role: 'user', content });

    // Auto-rename thread from first user message
    const thread = threads.value.find(t => t.id === activeThreadId.value);
    if (thread) {
      const isGenericName = /^(New Thread|Thread \d+|Default)$/i.test(thread.name);
      const userMsgCount = thread.messages.filter(m => m.role === 'user').length;
      if (isGenericName && userMsgCount === 1) {
        const snippet = content.trim().replace(/\s+/g, ' ').substring(0, 35);
        thread.name = snippet + (content.trim().length > 35 ? '…' : '');
        debugLog('chat.thread.auto-rename', { id: thread.id, name: thread.name });
      }
    }

    isLoading.value = true;
    streamingMessage.value = '';
    progressLines.value = [];
    debugLog('chat.stream.init', { streamingMessage: '' });

    // Track progress lines for this request so we can attach them to the assistant message
    let currentProgressLines = [];

    try {
      const headers = { 'Content-Type': 'application/json' };

      // Attach Supabase access token if available
      const token = await getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Use direct backend URL if configured, otherwise go through Vercel proxy
      const directUrl = typeof window !== 'undefined' && window.OPENCLAW_API_BASE_URL;
      const useProxy = !directUrl || window.OPENCLAW_USE_SAME_ORIGIN_API_PROXY !== false;
      const streamUrl = useProxy ? '/api/agent/stream' : `${directUrl}/api/agent/stream`;

      abortController = new AbortController();

      const response = await fetch(streamUrl, {
        method: 'POST',
        headers,
        signal: abortController.signal,
        body: JSON.stringify({
          message: content,
          pageScope: config.value.scope,
          book: config.value.book,
          allowTerminal: config.value.terminalAccess,
          history: messages.value
            .filter(m => m.role === 'user' || m.role === 'assistant')
            .map(m => ({ role: m.role, content: m.content })),
        }),
      });

      debugLog('chat.stream.response', { status: response.status, ok: response.ok });

      if (!response.ok) {
        let detail = `Stream failed (${response.status})`;
        try {
          const errBody = await response.json();
          if (errBody?.message) detail = errBody.message;
          else if (errBody?.error) detail = errBody.error;
        } catch (_) {}
        throw new Error(detail);
      }
      isConnected.value = true;

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let deltaCount = 0;
      let sseBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          debugLog('chat.stream.done', { deltaCount, finalMessage: streamingMessage.value.substring(0, 50) });
          break;
        }

        sseBuffer += decoder.decode(value, { stream: true });
        const parts = sseBuffer.split('\n\n');
        sseBuffer = parts.pop() || '';

        for (const block of parts) {
          const blockLines = block.split('\n');
          let eventType = 'message';
          let dataStr = '';
          for (const line of blockLines) {
            if (line.startsWith('event:')) eventType = line.slice(6).trim() || 'message';
            if (line.startsWith('data:')) dataStr += (dataStr ? '\n' : '') + line.slice(5).trim();
          }
          if (!dataStr) continue;

          try {
            const data = JSON.parse(dataStr);
            if (eventType === 'delta' && data.content) {
              deltaCount++;
              streamingMessage.value += data.content;
              if (deltaCount % 10 === 0) {
                debugLog('chat.stream.delta', { count: deltaCount, contentLength: streamingMessage.value.length });
              }
            } else if (eventType === 'status' && data.message) {
              const pl = addProgressLine(data.message);
              currentProgressLines.push(pl);
            } else if (eventType === 'tool_start' && data.name) {
              const pl = addProgressLine(`Using tool: ${data.name}`);
              currentProgressLines.push(pl);
            } else if (eventType === 'tool_result' && data.name) {
              const pl = addProgressLine(`Completed: ${data.name}`);
              currentProgressLines.push(pl);
            } else if (eventType === 'tool_error' && data.name) {
              const errMsg = data.message || 'Tool failed';
              const pl = addProgressLine(`Error: ${data.name} — ${errMsg}`);
              currentProgressLines.push(pl);
            } else if (eventType === 'final') {
              debugLog('chat.stream.complete', { finalLength: streamingMessage.value.length });
              const finalContent = data.content || streamingMessage.value;
              if (finalContent) {
                const msg = { role: 'assistant', content: finalContent };
                if (data.chart) msg.chart = data.chart;
                if (data.charts && data.charts.length) msg.charts = data.charts;
                // Attach progress lines to this message so they persist at full size
                if (currentProgressLines.length > 0) {
                  msg.progressLines = [...currentProgressLines];
                }
                addMessage(msg);
              }
              streamingMessage.value = '';
              progressLines.value = [];
              scheduleSave();
            } else if (eventType === 'error') {
              debugLog('chat.stream.error', { error: data.content || data.message });
              addMessage({ role: 'system', content: `Error: ${data.content || data.message || 'Stream error'}` });
              streamingMessage.value = '';
            }
          } catch (e) {
            debugLog('chat.stream.parse.error', { eventType, data: dataStr.substring(0, 50), error: e.message });
          }
        }
      }
    } catch (e) {
      // If the user aborted, don't show an error — the partial message was already saved
      if (e.name === 'AbortError') {
        debugLog('chat.stream.aborted');
      } else {
        debugLog('chat.stream.catch', { error: e.message });
        console.warn('Chat stream failed:', e);
        if (e.message.includes('Authentication required')) {
        const authStore = useAuthStore();
        authStore.showAuthGate = true;
        addMessage({ role: 'system', content: 'Please sign in to use the chat.' });
      } else {
        addMessage({ role: 'system', content: `Error: ${e.message}` });
      }
      }
    } finally {
      abortController = null;
      isLoading.value = false;
      isConnected.value = false;
      debugLog('chat.send.complete', { isLoading: false });
    }
  }

  function setConfig(newConfig) {
    debugLog('chat.config.update', { old: config.value, new: newConfig });
    config.value = { ...config.value, ...newConfig };
    scheduleSave();
  }

  debugLog('chat.store.init', { initialScope: config.value.scope, initialBook: config.value.book });

  return {
    messages,
    threads,
    activeThreadId,
    activeThread,
    isLoading,
    isConnected,
    streamingMessage,
    progressLines,
    config,
    isLoaded,
    addMessage,
    clearMessages,
    createThread,
    switchThread,
    deleteThread,
    renameThread,
    sendMessage,
    stopStreaming,
    setConfig,
    addProgressLine,
    clearProgress,
    loadFromServer,
  };
});